// Who owns each item needed from Amazon, Amazon's answers on them, and an item's history.
// Mirrors src/inrange_agents/lambda_handlers/api/amazon_overview/{owners,assignments_set,
// answers_*,item_history}.py (docs/Y2026-Q4/design-amazon-item-owners.md).
import { useMutation, useQuery, useQueryClient } from "react-query";

import type { PostJsonError } from "../http/postJson";
import type { AmazonNeed } from "./useAmazonOverview";
import { QK } from "../queryKeys";
import { useAgentsHttp } from "../useAgentsHttp";

/** A person's id, "inrange", or null for nobody. */
export type Assignee = string | null;

export const INRANGE = "inrange";

export interface OverviewPerson {
  person_id: string;
  name: string;
  /** InRange only. */
  email?: string;
  /** Taken off the share list since being given an item. */
  no_access?: boolean;
}

export interface AnswerEvent {
  at: string;
  /** "21 Oct 2026 at 09:12", London time. */
  when: string;
  kind: "submitted" | "asked" | "replied" | "approved" | "declined" | "dismissed" | "not_recorded";
  side: "amazon" | "inrange";
  name: string;
  text: string;
  email?: string;
}

export interface ItemAnswer {
  answer_id: string;
  project_id: string;
  item_key: string;
  kind: "resolved" | "inrange";
  status: "pending" | "asked" | "approved" | "declined" | "dismissed";
  /** Who had it before it was assigned to InRange. */
  previous: string | null;
  events: AnswerEvent[];
  question_id?: string | null;
}

export interface Owners {
  me: string | null;
  people: OverviewPerson[];
  former: OverviewPerson[];
  owners: { project_id: string; item_key: string; assignee: Assignee }[];
  answers: ItemAnswer[];
}

export interface HistoryLine {
  at: string;
  when: string;
  who: string;
  what: string;
  text: string;
  inrange_only: boolean;
  /** ``what`` with the source it came in as a citation to link (InRange only). */
  what_cited?: string;
}

export const itemId = (projectId: string, itemKey: string): string => `${projectId}\u0000${itemKey}`;

/** The key an item is owned and answered by: an open feedback point's own id, else the
 * item's key (amazon_item_owners.find_item reads it the same way). */
export const itemKeyOf = (n: Pick<AmazonNeed, "key" | "point_id">): string =>
  n.point_id ? `point:${n.point_id}` : n.key;

/** Whether an answer still shows on the item's line, and an approved mark as received, which
 * reads as received. */
export const answerShows = (a: ItemAnswer | undefined): boolean =>
  !!a && (a.status === "pending" || a.status === "asked" || a.status === "declined");

/** Assigned to InRange and not yet decided: its owner moves only by InRange's decision
 * (amazon_item_owners._waits_on_inrange). */
export const waitsOnInRange = (a: ItemAnswer | undefined): boolean =>
  !!a && a.kind === "inrange" && (a.status === "pending" || a.status === "asked");

/** How long an approved mark as received reads as received before the keeper's own record does:
 * the keeper records it on its next pass, a day at most. */
const APPROVAL_SHOWN_DAYS = 7;

/** An approved mark as received, which reads as received until the keeper records it. Past the
 * window the item reads as its record says, so a later re-grade is never masked. */
export const approvedResolved = (a: ItemAnswer | undefined, now: Date = new Date()): boolean => {
  if (!a || a.kind !== "resolved" || a.status !== "approved") return false;
  const approved = a.events.find((e) => e.kind === "approved");
  if (!approved) return false;
  return now.getTime() - new Date(approved.at).getTime() < APPROVAL_SHOWN_DAYS * 86_400_000;
};

export const useAmazonOwners = () => {
  const http = useAgentsHttp();
  return useQuery<Owners>([QK.amazonOwners], () => http<Owners>("/amazon-overview/owners", {}), {
    refetchOnWindowFocus: true,
  });
};

const message = (err: PostJsonError, conflict: string): Error => {
  if (err.status === 409) return new Error(conflict);
  try {
    const body = JSON.parse(err.bodyText ?? "") as { message?: string };
    if (body.message) return new Error(body.message);
  } catch {
    // Not JSON: the status line says what happened.
  }
  return err;
};

export interface AssignInput {
  project_id: string;
  item_key: string;
  assignee: Assignee;
  expected: Assignee;
  reason?: string;
}

export const ASSIGNMENT_MOVED =
  "Not saved: someone changed who owns this item a moment ago. The page now shows who; check and try again.";

const useOwnersMutation = <TInput>(path: string, conflict: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<unknown, Error, TInput>(
    (body) =>
      http<unknown>(path, body).catch((err: PostJsonError) => {
        throw message(err, conflict);
      }),
    {
      // Each control shows its own error beside it.
      meta: { silentError: true },
      onSettled: () => {
        void qc.invalidateQueries([QK.amazonOwners]);
        void qc.invalidateQueries([QK.amazonItemHistory]);
      },
    },
  );
};

export const useAssignItem = () =>
  useOwnersMutation<AssignInput>("/amazon-overview/assignments/set", ASSIGNMENT_MOVED);

export interface AnswerInput {
  project_id: string;
  item_key: string;
  text: string;
}

export const useMarkResolved = () =>
  useOwnersMutation<AnswerInput>("/amazon-overview/answers/submit", ASSIGNMENT_MOVED);

export const useReplyToInRange = () =>
  useOwnersMutation<AnswerInput & { answer_id: string }>(
    "/amazon-overview/answers/reply",
    ASSIGNMENT_MOVED,
  );

export const useDismissAnswer = () =>
  useOwnersMutation<{ project_id: string; item_key: string; answer_id: string }>(
    "/amazon-overview/answers/dismiss",
    ASSIGNMENT_MOVED,
  );

export interface BulkItem {
  project_id: string;
  item_key: string;
}

export interface BulkResult {
  done: BulkItem[];
  /** Left as they were, each with why (changed meanwhile, already answered, gone). */
  skipped: (BulkItem & { reason: string })[];
}

const useBulkMutation = <TInput>(path: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<BulkResult, Error, TInput>(
    (body) =>
      http<BulkResult>(path, body).catch((err: PostJsonError) => {
        throw message(err, ASSIGNMENT_MOVED);
      }),
    {
      meta: { silentError: true },
      onSettled: () => {
        void qc.invalidateQueries([QK.amazonOwners]);
        void qc.invalidateQueries([QK.amazonItemHistory]);
      },
    },
  );
};

/** Give several items to one person, to InRange (one reason for all), or clear them. */
export const useBulkAssign = () =>
  useBulkMutation<{
    items: (BulkItem & { expected: Assignee })[];
    assignee: Assignee;
    reason?: string;
  }>("/amazon-overview/assignments/set-many");

/** Amazon marks several items received, saying how once for all. */
export const useBulkResolve = () =>
  useBulkMutation<{ items: BulkItem[]; text: string }>("/amazon-overview/answers/submit-many");

export const useItemHistory = (projectId: string, itemKey: string, enabled: boolean) => {
  const http = useAgentsHttp();
  return useQuery<HistoryLine[]>(
    [QK.amazonItemHistory, projectId, itemKey],
    async () =>
      (
        await http<{ lines: HistoryLine[] }>("/amazon-overview/item-history", {
          project_id: projectId,
          item_key: itemKey,
        })
      ).lines,
    { enabled },
  );
};

export interface ItemChange {
  at: string;
  when: string;
  kind: string;
  /** How the banner counts it. */
  label: string;
  what: string;
  who: string;
}

export interface Changes {
  /** The end of the viewer's previous visit; null on their first, and for InRange. */
  since: string | null;
  items: { project_id: string; item_key: string; changes: ItemChange[] }[];
}

/** What changed on the viewer's own items since their last login (Amazon's people only). */
export const useAmazonChanges = (enabled: boolean) => {
  const http = useAgentsHttp();
  // Read once per visit: what changed since the last login does not move while they stay.
  return useQuery<Changes>([QK.amazonChanges], () => http<Changes>("/amazon-overview/changes", {}), {
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};
